// src/modals/PrelegentModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faLinkedin, faYoutube } from "@fortawesome/free-brands-svg-icons";

/* ------------------------- CONST ------------------------- */
const API = "https://api18081113.queroapp.pl";

const FALLBACK_BG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="300" viewBox="0 0 1200 300">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#d1d5db"/>
      </linearGradient></defs>
      <rect width="1200" height="300" fill="url(#g)"/>
    </svg>`
  );

/* ------------------------- LOG ------------------------- */
const log  = (...a) => console.log  ("%c[PM]", "color:#0ea5e9;font-weight:700;", ...a);
const warn = (...a) => console.warn ("%c[PM]", "color:#f59e0b;font-weight:700;", ...a);
const err  = (...a) => console.error("%c[PM]", "color:#ef4444;font-weight:700;", ...a);

async function fetchJson(url, init) {
  const t0 = performance.now();
  const res = await fetch(url, { mode: "cors", ...init });
  const text = await res.text();
  let json = null;
  try { json = JSON.parse(text); } catch {}
  log("HTTP", { url, status: res.status, ok: res.ok, ms: Math.round(performance.now() - t0) });
  return { res, json, text };
}

/* ------------------------- HELPERS ------------------------- */
const normalizeMedia = (val, kind /* "img" | "bg" */) => {
  if (val == null) return "";
  let s = String(val).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.includes("/")) {
    s = s.replace(/^\/+/, "");
    return `https://eventicon.queroapp.pl/${s}`;
  }
  const subdir = kind === "img" ? "prelegent_photo" : "prelegent_bg";
  return `https://eventicon.queroapp.pl/${subdir}/${s}`;
};

const isFullSegmentName = (name, sec) => !!(name && sec && String(name).includes(`_${sec}_`));
const toShort = (full, sec) =>
  isFullSegmentName(full, sec) ? String(full).split(`_${sec}_`)[0].replace(/_/g, " ") : String(full || "").replace(/_/g, " ");
const toShortUnderscore = (full, sec) => toShort(full, sec).trim().replace(/\s+/g, "_");

const pickSocial = (raw) => {
  if (!raw) return null;
  let href = String(raw).trim();
  if (!/^https?:\/\//i.test(href)) href = `https://${href}`;
  try {
    const host = new URL(href).host.toLowerCase();
    if (host.includes("facebook.")) return { icon: faFacebook, label: "Facebook", href };
    if (host.includes("instagram.")) return { icon: faInstagram, label: "Instagram", href };
    if (host.includes("linkedin.") || host.includes("lnkd.in")) return { icon: faLinkedin, label: "LinkedIn", href };
    if (host.includes("youtube.") || host.includes("youtu.be")) return { icon: faYoutube, label: "YouTube", href };
  } catch {}
  return { icon: faGlobe, label: "Website", href };
};

/* ------------------------- COMPONENT ------------------------- */
/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - prelegent: { segment_name: string, prelegent_*?: string }  // może być pusty
 * - secretId?: string         // jeśli nie podasz, weźmie z localStorage
 * - edit?: boolean            // << HAK: włącz tryb edycji tylko gdy true
 * - onSaved?: () => void      // callback po udanym zapisie
 * - debug?: boolean
 */
export default function PrelegentModal({
  isOpen,
  onClose,
  prelegent,
  secretId: secretIdProp,
  edit = false,
  onSaved,
  debug = false,
}) {
  const secretId = useMemo(() => secretIdProp || localStorage.getItem("secretId") || "", [secretIdProp]);
  const fullSeg  = String(prelegent?.segment_name || "").trim();
  const shortSeg = useMemo(() => toShort(fullSeg, secretId), [fullSeg, secretId]);

  // form / view state
  const [name, setName] = useState("");
  const [info, setInfo] = useState("");
  const [link, setLink] = useState("");
  const [location, setLocation] = useState("");

  const [avatarUrl, setAvatarUrl] = useState("");
  const [bgUrl, setBgUrl] = useState(FALLBACK_BG);
  const [avatarFailed, setAvatarFailed] = useState(false);

  // files (tylko w trybie edit używamy)
  const [avatarFile, setAvatarFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const avatarFileRef = useRef(null);
  const bgFileRef = useRef(null);

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  /* --------- seed z props + fetch z GET /get-prelegent-info/:id --------- */
  useEffect(() => {
    if (!isOpen) return;

    // 1) seed z props
    const p = prelegent || {};
    setName((p.prelegent_name || "").trim());
    setInfo((p.prelegent_info || "").trim());
    setLink((p.prelegent_link || "").trim());
    setLocation((p.prelegent_location || "").trim());

    const pImg = normalizeMedia(p.prelegent_img ?? p["prelegent-img"] ?? "", "img");
    const pBg  = normalizeMedia(p.prelegent_bg ?? p["prelegent-bg"] ?? "", "bg");
    setAvatarFailed(false);
    setAvatarUrl(pImg || "");
    setBgUrl(pBg || FALLBACK_BG);
    setAvatarFile(null);
    setBgFile(null);
    if (avatarFileRef.current) avatarFileRef.current.value = "";
    if (bgFileRef.current) bgFileRef.current.value = "";

    // 2) fetch brakujących pól (lub całego wiersza)
    if (!secretId || !fullSeg) return;

    (async () => {
      setLoading(true);
      setError("");
      setMsg("");
      try {
        const { res, json, text } = await fetchJson(`${API}/get-prelegent-info/${encodeURIComponent(secretId)}`);
        const message = String(json?.message || json?.error || text || "");

        if (!res.ok) {
          // stary backend bez kolumny prelegent_bg
          if (/Unknown column\s+'prelegent_bg'/i.test(message)) {
            warn("legacy backend (bez prelegent_bg) – ładuję bez tła");
          } else {
            throw new Error(message || "Błąd pobierania prelegenta");
          }
        }

        const rows =
          Array.isArray(json?.data) ? json.data :
          Array.isArray(json)       ? json       : [];

        // dopasuj po pełnej nazwie, a jakby co – po prefiksie short_<secretId>_
        const shortUnderscore = toShortUnderscore(fullSeg, secretId).toLowerCase();
        const row =
          rows.find((r) => (r?.segment_name || r?.segment) === fullSeg) ||
          rows.find((r) => String(r?.segment_name || r?.segment || "").toLowerCase()
                            .startsWith(`${shortUnderscore}_${String(secretId).toLowerCase()}_`)) ||
          null;

        if (debug) log("[modal load row]", { found: !!row, fullSeg });

        if (row) {
          if (!p.prelegent_name)      setName(row.prelegent_name || "");
          if (!p.prelegent_info)      setInfo(row.prelegent_info || "");
          if (!p.prelegent_link)      setLink(row.prelegent_link || "");
          if (!p.prelegent_location)  setLocation(row.prelegent_location || "");

          const img = normalizeMedia(row.prelegent_img || row["prelegent-img"] || "", "img");
          const bg  = normalizeMedia(row.prelegent_bg || row["prelegent-bg"] || "", "bg");
          if (!pImg && img) setAvatarUrl(img);
          if (!pBg  && bg ) setBgUrl(bg);
        }
      } catch (e) {
        setError(e?.message || "Nie udało się pobrać danych prelegenta.");
        err("LOAD ERROR", e);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fullSeg, secretId]);

  /* ------------------------- SAVE (tylko gdy edit) ------------------------- */
  const saveTextFieldsJSON = async () => {
    const payload = {
      secretId,
      segment_name: fullSeg,
      prelegent_name: name || "",
      prelegent_info: info || "",
      prelegent_link: link || "",
      prelegent_location: location || "",
    };
    const { res, json, text } = await fetchJson(`${API}/add-prelegent-info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok || json?.success === false) {
      throw new Error(json?.message || text || "JSON rejected");
    }
  };

  const saveTextFieldsForm = async () => {
    const fd = new FormData();
    fd.append("secretId", secretId);
    fd.append("secred-id", secretId); // kompatybilność z backendem
    fd.append("segment_name", fullSeg);
    fd.append("prelegent_name", name || "");
    fd.append("prelegent_info", info || "");
    fd.append("prelegent_link", link || "");
    fd.append("prelegent_location", location || "");

    const { res, json, text } = await fetchJson(`${API}/add-prelegent-info`, { method: "POST", body: fd });
    if (!res.ok || json?.success === false) {
      throw new Error(json?.message || text || "FormData rejected");
    }
  };

  const uploadAvatarIfNeeded = async () => {
    if (!avatarFile) return;
    const fd = new FormData();
    fd.append("secred-id", secretId);
    fd.append("segment_name", fullSeg);
    fd.append("prelegent-img", avatarFile);
    const { res, json, text } = await fetchJson(`${API}/upload-prelegent-image`, { method: "POST", body: fd });
    if (!res.ok || !json?.success) throw new Error(json?.message || text || "Avatar upload failed");
  };

  const uploadBgIfNeeded = async () => {
    if (!bgFile) return; // legacy safe
    const fd = new FormData();
    fd.append("secred-id", secretId);
    fd.append("segment_name", fullSeg);
    fd.append("prelegent-bg", bgFile);
    const { res, json, text } = await fetchJson(`${API}/upload-prelegent-bg`, { method: "POST", body: fd });
    if (!res.ok || !json?.success) throw new Error(json?.message || text || "BG upload failed");
  };

  const handleSave = async () => {
    if (!edit) return;
    if (!secretId || !isFullSegmentName(fullSeg, secretId)) {
      setError("Brak pełnej nazwy segmentu lub secretId – zapis zablokowany.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMsg("");

      try { await saveTextFieldsJSON(); }
      catch (e) { warn("JSON save failed → try FormData", e?.message); await saveTextFieldsForm(); }

      try { await uploadAvatarIfNeeded(); } catch (e) { warn("Avatar upload warn", e?.message); }
      try { await uploadBgIfNeeded(); }     catch (e) { warn("BG upload warn", e?.message); }

      setMsg("Zapisano.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Coś poszło nie tak podczas zapisu.");
      err("SAVE ERROR", e);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const displayName = (name || "").trim() || "—";
  const firstLetter = (displayName[0] || "—").toUpperCase();
  const displayInfo = (info || "").trim();
  const displayLocation = (location || "").trim();
  const displayTagline = displayInfo
    ? displayInfo.split(/[\|\•]+/g).map((s) => s.trim()).filter(Boolean).join(" • ")
    : "";
  const social = pickSocial(link);

  return (
    <div className="plg-overlay" role="dialog" aria-modal="true">
      <div className="plg-modal">
        <button type="button" className="plg-close" aria-label="Zamknij" onClick={onClose}>
          ×
        </button>

        {/* COVER */}
        <div className="plg-cover">
          <img
            src={bgUrl || FALLBACK_BG}
            alt="cover"
            onError={() => { if (bgUrl !== FALLBACK_BG) setBgUrl(FALLBACK_BG); }}
          />
          {edit && (
            <>
              <button className="plg-cover-edit" type="button" onClick={() => bgFileRef.current?.click()}>
                Edytuj tło
              </button>
              <input
                ref={bgFileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setBgFile(f); setBgUrl(URL.createObjectURL(f)); }
                }}
              />
            </>
          )}
        </div>

        {/* AVATAR */}
        <div className="plg-avatar-wrap">
          {!avatarFailed && avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="plg-avatar"
              width={160}
              height={160}
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <div className="plg-avatar-fallback">{firstLetter}</div>
          )}
          {edit && (
            <>
              <button className="plg-avatar-edit" type="button" onClick={() => avatarFileRef.current?.click()}>
                Edytuj zdjęcie
              </button>
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setAvatarFile(f); setAvatarUrl(URL.createObjectURL(f)); setAvatarFailed(false); }
                }}
              />
            </>
          )}
        </div>

        {/* CONTENT */}
        <div className="plg-content">
       

          {error && <p className="plg-msg plg-msg--error">{error}</p>}
          {msg && !error && <p className="plg-msg">{msg}</p>}
          {loading && <p className="plg-muted">Ładowanie…</p>}

          {!edit ? (
            <>
              <h1 className="plg-name">{displayName}</h1>
              {displayTagline ? <p className="plg-tagline">{displayTagline}</p> : null}
              {displayLocation ? (
                <p className="plg-location">
                  <span className="plg-location-label">Location:</span> {displayLocation}
                </p>
              ) : null}
              {social ? (
                <div className="plg-socials">
                  <a href={social.href} target="_blank" rel="noopener noreferrer" className="plg-icon-link" aria-label={social.label} title={social.href}>
                    <FontAwesomeIcon icon={social.icon} />
                  </a>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <label className="plg-label" htmlFor="pm-name">Imię i nazwisko</label>
              <input id="pm-name" className="plg-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Jan Kowalski" />

              <label className="plg-label" htmlFor="pm-info">Opis / Bio</label>
              <textarea id="pm-info" className="plg-textarea" rows={3} value={info} onChange={(e) => setInfo(e.target.value)} placeholder="Krótki opis, stanowisko, tematyka…" />

              <label className="plg-label" htmlFor="pm-link">Link (opcjonalnie)</label>
              <input id="pm-link" className="plg-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Wklej link (FB/IG/LinkedIn/YouTube lub www)" type="url" />

              <label className="plg-label" htmlFor="pm-loc">Lokalizacja (opcjonalnie)</label>
              <input id="pm-loc" className="plg-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Miasto / kraj" />
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="plg-footer">
          <button className="plg-btn" onClick={onClose} disabled={saving}>Zamknij</button>
          {edit && (
            <button className="plg-btn plg-btn--primary" onClick={handleSave} disabled={saving || loading || !secretId || !isFullSegmentName(fullSeg, secretId)}>
              {saving ? "Zapisywanie…" : "Zapisz"}
            </button>
          )}
        </div>

        {debug && (
          <div className="plg-debug">
            <strong>DEBUG</strong>
            <div><b>segment_name:</b> <code>{fullSeg || "(brak)"}</code></div>
            <div><b>secretId:</b> <code>{secretId || "(brak)"}</code></div>
            <div><b>edit:</b> <code>{String(edit)}</code></div>
          </div>
        )}
      </div>

      {/* <style>{`
        .plg-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:9999}
        .plg-modal{position:relative;width:min(760px,94vw);background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 20px 40px rgba(0,0,0,.28)}
        .plg-close{position:absolute;top:10px;right:12px;width:36px;height:36px;border:0;border-radius:50%;background:#f3f4f6;cursor:pointer;font-size:22px;line-height:1;z-index:10}
        .plg-close:hover{background:#e5e7eb}
        .plg-cover{width:100%;height:200px;overflow:hidden;background:#f3f4f6;position:relative}
        .plg-cover img{width:100%;height:100%;object-fit:cover;display:block}
        .plg-cover-edit{position:absolute;inset:auto 12px 12px auto;padding:8px 12px;border:0;border-radius:10px;background:#111827cc;color:#fff}
        .plg-avatar-wrap{position:absolute;top:140px;left:50%; transform:translate(-50%,0); width:160px;height:160px;border-radius:50%;padding:4px;background:#fff;box-shadow:0 6px 20px rgba(0,0,0,.25)}
        .plg-avatar{width:100%;height:100%;border-radius:50%;object-fit:cover}
        .plg-avatar-fallback{width:100%;height:100%;border-radius:50%;display:grid;place-items:center;background:#e5e7eb;font-weight:700;font-size:48px;color:#111827}
        .plg-avatar-edit{position:absolute;left:50%;bottom:8px;transform:translateX(-50%);padding:6px 10px;border:0;border-radius:10px;background:#111827cc;color:#fff}
        .plg-content{padding:96px 20px 16px}
        .plg-title{margin:0 0 8px;font-size:18px;font-weight:800;color:#6b7280;text-transform:uppercase;letter-spacing:.06em}
        .plg-name{margin:8px 0 6px;font-size:32px;font-weight:700;color:#111827;text-align:center}
        .plg-tagline{margin:0 auto 10px;max-width:640px;max-height:100px; overflow:scroll;color:#4b5563;font-size:15.5px;line-height:1.55;text-align:center}
        .plg-location{margin:6px 0 0;font-size:16px;color:#111827;text-align:center}
        .plg-location-label{font-weight:700}
        .plg-socials{margin-top:12px;text-align:center}
        .plg-icon-link{font-size:28px;color:#111827;transition:opacity .2s}
        .plg-icon-link:hover{opacity:.85}
        .plg-label{display:block;font-weight:600;margin:12px 0 6px}
        .plg-input,.plg-textarea{width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px}
        .plg-textarea{resize:vertical}
        .plg-footer{display:flex;justify-content:flex-end;gap:10px;padding:12px 16px;border-top:1px solid #e5e7eb;background:#fafafa}
        .plg-btn{padding:10px 14px;border-radius:10px;border:1px solid #d1d5db;background:#fff;cursor:pointer}
        .plg-btn--primary{background:#6366f1;border-color:#6366f1;color:#fff}
        .plg-msg{margin:8px 0 0;color:#10b981;font-weight:600;text-align:center}
        .plg-msg--error{color:#ef4444}
        .plg-muted{color:#6b7280;text-align:center}
        .plg-debug{font-family:ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace;font-size:12px;background:#111827;color:#f9fafb;padding:10px 12px;border-top:1px solid #374151}
        .plg-debug code{color:#93c5fd}
      `}</style> */}
    </div>
  );
}
